import {Exclude, Expose, plainToInstance, Type} from "class-transformer";
import * as t from "io-ts";
import {DriverLocationArrayValidator, DriverLocationEntity} from "./driver-location";
import {GeofenceEventArrayValidator, GeofenceEventEntity} from "./geofence-event";
import {PersonEntity, PersonValidator} from "./person";
import {ShiftArrayValidator, ShiftEntity} from "./shift";
import {TrailerEntity, TrailerValidator} from "./trailer";
import {TransportArrayValidator, TransportEntity} from "./transport";
import {Default} from "../util/decorators";

/** Validator for Shift JSON */
export const LocationHistoryValidator = t.intersection([
  t.type({
    shiftList: ShiftArrayValidator,
    driverLocationList: DriverLocationArrayValidator,
    transportList: TransportArrayValidator,
    geofenceEventList: GeofenceEventArrayValidator,
  }),
  t.partial({
    trailer: TrailerValidator,
    person: PersonValidator,
    fromDttm: t.number,
    toDttm: t.number,
  }),
]);

export type LocationHistory = t.TypeOf<typeof LocationHistoryValidator>;

@Exclude()
export class LocationHistoryEntity {
  @Expose({name: 'shiftList'})
  @Type(() => ShiftEntity)
  @Default([])
  shifts: ShiftEntity[] = [];

  @Expose({name: 'driverLocationList'})
  @Type(() => DriverLocationEntity)
  @Default([])
  driverLocations: DriverLocationEntity[] = [];

  @Expose({name: 'transportList'})
  @Type(() => TransportEntity)
  @Default([])
  transports: TransportEntity[] = [];

  @Expose({name: 'geofenceEventList'})
  @Type(() => GeofenceEventEntity)
  @Default([])
  geofenceEvents: GeofenceEventEntity[] = [];

  @Expose()
  @Type(() => TrailerEntity)
  trailer?: TrailerEntity;

  @Expose()
  @Type(() => PersonEntity)
  person?: PersonEntity;

  @Expose()
  fromDttm?: number;

  @Expose()
  toDttm?: number;

  /**
   * Constructor for LocationHistoryEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(locationHistory: LocationHistory): LocationHistoryEntity {
    return plainToInstance(
      LocationHistoryEntity,
      locationHistory,
      {
        excludeExtraneousValues: true,
      }
    );
  }
}

export type LocationHistoryEvent =
  | TransportEntity
  | GeofenceEventEntity
  | DriverLocationEntity;

export const isLocationEntityGeofenceEvent = (
  value: LocationHistoryEvent
): value is GeofenceEventEntity => {
  return value.entityType === 'GEOFENCEEVENT';
};

export const isLocationEntityTransport = (
  value: LocationHistoryEvent
): value is TransportEntity => {
  return value.entityType === 'TRANSPORT';
};

export const isLocationEntityDriverLocation = (
  value: LocationHistoryEvent
): value is DriverLocationEntity => {
  return value.entityType === 'DRIVERLOCATION';
};

export type ShiftLocationHistoryMetadata = {
  transportIds: string[];
  minDttm?: number;
  maxDttm?: number;
};

/** Validator for location history search criteria. */
export const LocationHistorySearchCriteriaValidator = t.intersection([
  t.type({
    fromDttm: t.number,
    toDttm: t.number,
  }),
  t.partial({
    companyId: t.string,
    trailerId: t.string,
    personId: t.string,
    regionId: t.string,
    operatorId: t.string,
    databaseLookup: t.string,
  }),
]);

export type LocationHistorySearchCriteria = t.TypeOf<
  typeof LocationHistorySearchCriteriaValidator
>;
